import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dropzone from "react-dropzone";
import ZipPopup from "../../../../../../../GlobalComponents/Popup/ZipPopup";
import { Actions } from "../../../../../../../redux/constants/actionTypes";
import { CircularProgress, LinearProgress } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import MinimizeIcon from "@mui/icons-material/Minimize";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import styles from "../FileExplorer.module.scss";
import {
  expandZipFiles,
  filterJunkFiles,
  stripCommonWrapper,
} from "../utils/zipUtils";

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

const FILES_PER_FOLDER = 60;

const STEPS = [
  { id: 1, label: "Select" },
  { id: 2, label: "Review" },
  { id: 3, label: "Confirm" },
];

const getRelPath = (file) => {
  const p =
    file.relativePath || file.path || file.webkitRelativePath || file.name || "";
  return p.replace(/^\.?\//, "");
};

const buildTree = (files) => {
  const root = { folders: {}, files: [] };
  files.forEach((file) => {
    const parts = getRelPath(file).split("/").filter(Boolean);
    const fileName = parts.pop();
    let node = root;
    parts.forEach((part) => {
      if (!node.folders[part]) node.folders[part] = { folders: {}, files: [] };
      node = node.folders[part];
    });
    node.files.push({ name: fileName, size: file?.size || 0 });
  });
  return root;
};

const collectTopFolderPaths = (root) => Object.keys(root.folders);

const UploadPopup = (props) => {
  const {
    uploadPopup,
    uploadDetails,
    setUploadDetails,
    uploadSelectedFiles,
    flushStateVariables,
    loading,
    total,
    setClose,
  } = props;

  const dispatch = useDispatch();
  // Progress + minimise/complete UI state live in redux so the app-wide
  // floating widget can keep showing after this page unmounts. The wizard
  // (step / expanded folders) stays local — it only matters while on-page.
  const uploadProgress = useSelector((state) => state.sitesInfo?.uploadProgress);
  const uploadInProgress = !!uploadProgress?.active;
  const minimized = !!uploadProgress?.minimized;
  const completed = !!uploadProgress?.completed;
  const completedTotal = uploadProgress?.total || 0;
  const progUploaded = uploadProgress?.uploaded || 0;
  const progTotal = uploadProgress?.total || 0;
  const value = progTotal > 0 ? (progUploaded / progTotal) * 100 : 0;

  const [step, setStep] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());

  const files = useMemo(
    () => uploadDetails?.filesSelected || [],
    [uploadDetails?.filesSelected],
  );
  const hasFiles = total > 0;

  const totalSize = useMemo(
    () => files.reduce((sum, f) => sum + (f?.size || 0), 0),
    [files],
  );

  const tree = useMemo(() => buildTree(files), [files]);

  const folderCount = useMemo(() => {
    let count = 0;
    const walk = (node) => {
      Object.values(node.folders).forEach((child) => {
        count += 1;
        walk(child);
      });
    };
    walk(tree);
    return count;
  }, [tree]);

  // Reset the wizard to step 1 whenever the popup is opened without an upload
  // already running (a fresh selection). If we land here mid-upload (e.g. via
  // the widget's "Expand"), leave the uploading view alone.
  useEffect(() => {
    if (uploadPopup && !uploadInProgress) setStep(1);
  }, [uploadPopup, uploadInProgress]);

  useEffect(() => {
    if (step === 2) setExpanded(new Set(collectTopFolderPaths(tree)));
  }, [step, tree]);

  // The "don't refresh mid-upload" guard now lives in the app-wide
  // GlobalUploadWidget so it protects the upload on every page, not just here.

  const handleClosePopup = () => {
    setStep(1);
    dispatch({ type: Actions.RESET_UPLOAD_PROGRESS });
    flushStateVariables();
    setUploadDetails({ filesSelected: [], uploadingStatus: undefined });
  };

  const handleUpload = () => {
    uploadSelectedFiles();
    setClose(true);
  };

  const handleFilesSelected = async (fileList) => {
    if (uploadInProgress) return;
    const expanded = await expandZipFiles(Array.from(fileList || []));
    // Whole-folder selections carry OS junk (.DS_Store, __MACOSX, …) that the
    // backend 500s on — strip it, same as zip expansion already does.
    const cleaned = filterJunkFiles(expanded);
    // Upload the folder's CONTENTS directly (no "tekyantra/" wrapper folder).
    const selected = stripCommonWrapper(cleaned);
    setUploadDetails({ filesSelected: selected });
  };

  const clearSelection = () => {
    if (uploadInProgress) return;
    setUploadDetails({ filesSelected: [] });
    setStep(1);
  };

  const toggleFolder = (path) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  // While uploading, counts come from redux (progUploaded/progTotal); the
  // wizard's own `total` is the selection count before an upload starts.
  const uploadedCount = uploadInProgress
    ? progUploaded
    : Math.round((value / 100) * total);

  const renderTree = (node, depth, parentPath) => {
    const rows = [];
    const folderNames = Object.keys(node.folders).sort((a, b) =>
      a.localeCompare(b),
    );

    folderNames.forEach((name) => {
      const child = node.folders[name];
      const path = parentPath ? `${parentPath}/${name}` : name;
      const isOpen = expanded.has(path);
      const itemCount =
        Object.keys(child.folders).length + child.files.length;
      rows.push(
        <div
          key={`fol-${path}`}
          className={styles.treeFolder}
          style={{ paddingLeft: 10 + depth * 18 }}
          onClick={() => toggleFolder(path)}
        >
          {isOpen ? (
            <ExpandMoreIcon className={styles.treeChevron} fontSize="small" />
          ) : (
            <ChevronRightIcon className={styles.treeChevron} fontSize="small" />
          )}
          <FolderOutlinedIcon className={styles.treeFolderIcon} fontSize="small" />
          <span className={styles.treeName}>{name}</span>
          <span className={styles.treeCount}>
            {itemCount} item{itemCount > 1 ? "s" : ""}
          </span>
        </div>,
      );
      if (isOpen) rows.push(...renderTree(child, depth + 1, path));
    });

    node.files.slice(0, FILES_PER_FOLDER).forEach((file, i) => {
      rows.push(
        <div
          key={`file-${parentPath}-${i}`}
          className={styles.treeFile}
          style={{ paddingLeft: 10 + depth * 18 + 22 }}
        >
          <InsertDriveFileOutlinedIcon
            className={styles.treeFileIcon}
            fontSize="small"
          />
          <span className={styles.treeName}>{file.name}</span>
          <span className={styles.treeSize}>{formatBytes(file.size)}</span>
        </div>,
      );
    });
    if (node.files.length > FILES_PER_FOLDER) {
      rows.push(
        <div
          key={`more-${parentPath}`}
          className={styles.treeMore}
          style={{ paddingLeft: 10 + depth * 18 + 22 }}
        >
          + {node.files.length - FILES_PER_FOLDER} more files
        </div>,
      );
    }
    return rows;
  };

  const subHeader = uploadInProgress
    ? completed
      ? "Upload finished"
      : "Your files are uploading in the background"
    : step === 1
      ? "Step 1 · Choose the files you want to upload"
      : step === 2
        ? "Step 2 · Review how they’ll be organised"
        : "Step 3 · Confirm and start the upload";

  return (
    <>
      {/* The minimised progress widget is rendered app-wide (GlobalUploadWidget)
          so it survives navigating away from this page. */}
      <ZipPopup
        trigger={uploadPopup && !minimized}
        closePopup={handleClosePopup}
        header={"Upload Files"}
        subHeader={subHeader}
        removed={true}
        contentClassName={styles.uploadPopupBox}
        fullWidthContent
        fillHeight
      >
        <div className={styles.uploadModal}>
          {/* Minimise to run in background — top-right corner */}
          {uploadInProgress && !completed && (
            <button
              type="button"
              className={styles.minimizeBtn}
              onClick={() =>
                dispatch({
                  type: Actions.SET_UPLOAD_PROGRESS,
                  data: { minimized: true },
                })
              }
              title="Minimise & run in background"
            >
              <MinimizeIcon fontSize="small" />
            </button>
          )}

          {/* Stepper */}
          {!uploadInProgress && (
            <div className={styles.stepper}>
              {STEPS.map((s, idx) => (
                <div className={styles.stepItem} key={s.id}>
                  <div
                    className={`${styles.stepDot} ${
                      step === s.id ? styles.stepActive : ""
                    } ${step > s.id ? styles.stepDone : ""}`}
                  >
                    {step > s.id ? (
                      <CheckOutlinedIcon fontSize="inherit" />
                    ) : (
                      s.id
                    )}
                  </div>
                  <span
                    className={`${styles.stepLabel} ${
                      step === s.id ? styles.stepLabelActive : ""
                    }`}
                  >
                    {s.label}
                  </span>
                  {idx < STEPS.length - 1 && (
                    <span
                      className={`${styles.stepLine} ${
                        step > s.id ? styles.stepLineDone : ""
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className={styles.modalBody}>
            {uploadInProgress ? (
              // ---------- Uploading ----------
              <div className={styles.uploadingState}>
                <span
                  className={`${styles.iconCircle} ${
                    completed ? styles.iconCircleDone : ""
                  }`}
                >
                  {completed ? (
                    <CheckCircleIcon className={styles.uploadIcon} />
                  ) : (
                    <CloudUploadOutlinedIcon className={styles.uploadIcon} />
                  )}
                </span>
                <div className={styles.uploadingTitle}>
                  {completed ? "Upload complete" : "Uploading your files…"}
                </div>
                <div className={styles.uploadingSub}>
                  {completed
                    ? `All ${completedTotal} file${
                        completedTotal > 1 ? "s" : ""
                      } uploaded successfully.`
                    : "You can keep working — minimise to run this in the background."}
                </div>
                {!completed && (
                  <>
                    <div className={styles.progressRow}>
                      <span>
                        {uploadedCount} / {total} files
                      </span>
                      <span>{Math.round(value)}%</span>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={value}
                      className={styles.progressBar}
                    />
                    <div className={styles.uploadWarn}>
                      <WarningAmberOutlinedIcon fontSize="small" />
                      Please don’t refresh or close this page while uploading.
                    </div>
                  </>
                )}
              </div>
            ) : step === 1 ? (
              // ---------- Step 1: select ----------
              <>
                <Dropzone
                  onDrop={(acceptedFiles) => handleFilesSelected(acceptedFiles)}
                  disabled={loading}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <div
                      className={`${styles.dropzone} ${
                        isDragActive ? styles.dropzoneActive : ""
                      } ${loading ? styles.disabled : ""}`}
                      {...getRootProps()}
                    >
                      <input {...getInputProps()} />
                      <span className={styles.iconCircle}>
                        <CloudUploadOutlinedIcon className={styles.uploadIcon} />
                      </span>
                      <div className={styles.dropTitle}>
                        {isDragActive
                          ? "Drop the files here"
                          : "Drag & drop files or a .zip here"}
                      </div>
                      <div className={styles.dropSub}>
                        or{" "}
                        <span className={styles.dropLink}>click to browse</span>
                      </div>
                      <div className={styles.dropHint}>
                        Supports files and .zip archives (extracted automatically)
                      </div>
                    </div>
                  )}
                </Dropzone>

                {hasFiles && (
                  <div
                    className={`${styles.folderRow} ${styles.folderRowSelected}`}
                  >
                    <div className={styles.folderInfo}>
                      <CheckCircleOutlineIcon
                        className={styles.folderSelectedIcon}
                      />
                      <div className={styles.folderHint}>
                        <strong>
                          {total} file{total > 1 ? "s" : ""} selected
                        </strong>
                        <span>{formatBytes(totalSize)} · ready to upload</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.folderClearBtn}
                      onClick={clearSelection}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </>
            ) : step === 2 ? (
              // ---------- Step 2: review structure ----------
              <>
                <div className={styles.reviewSummary}>
                  This is how your files will be organised when uploaded.
                </div>
                <div className={styles.treeBox}>{renderTree(tree, 0, "")}</div>
              </>
            ) : (
              // ---------- Step 3: confirm ----------
              <div className={styles.confirmBox}>
                <span className={styles.iconCircle}>
                  <CloudUploadOutlinedIcon className={styles.uploadIcon} />
                </span>
                <div className={styles.confirmTitle}>Ready to upload</div>
                <div className={styles.confirmGrid}>
                  <div className={styles.confirmStat}>
                    <span className={styles.confirmNum}>{total}</span>
                    <span className={styles.confirmLabel}>Files</span>
                  </div>
                  <div className={styles.confirmStat}>
                    <span className={styles.confirmNum}>{folderCount}</span>
                    <span className={styles.confirmLabel}>Folders</span>
                  </div>
                  <div className={styles.confirmStat}>
                    <span className={styles.confirmNum}>
                      {formatBytes(totalSize)}
                    </span>
                    <span className={styles.confirmLabel}>Total size</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {uploadInProgress ? (
            completed ? (
              <div className={`${styles.footer} ${styles.footerCenter}`}>
                <button
                  type="button"
                  className={styles.uploadBtn}
                  onClick={handleClosePopup}
                >
                  <CheckOutlinedIcon fontSize="small" />
                  Done
                </button>
              </div>
            ) : null
          ) : (
            <div className={styles.footer}>
              {step === 1 ? (
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={handleClosePopup}
                >
                  Cancel
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setStep(step - 1)}
                >
                  <ArrowBackOutlinedIcon fontSize="small" />
                  Back
                </button>
              )}

              {step < 3 ? (
                <button
                  type="button"
                  className={styles.uploadBtn}
                  disabled={!hasFiles}
                  onClick={hasFiles ? () => setStep(step + 1) : undefined}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.uploadBtn}
                  disabled={!hasFiles || loading}
                  onClick={hasFiles ? handleUpload : undefined}
                >
                  {loading ? (
                    <CircularProgress size={16} color={"inherit"} />
                  ) : (
                    <>
                      <CloudUploadOutlinedIcon fontSize="small" />
                      Upload {total} file{total > 1 ? "s" : ""}
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </ZipPopup>
    </>
  );
};

export default UploadPopup;
